import React, { useState, useEffect } from 'react'
import ErrorModal from '../../ErrorModal'
import FormlessInterstitialWrapper from '../../FormlessInterstitial'
import { AttributionDetails } from '../types'
import EventsService, { AppFormEventParams } from '../../../services/events-service'
import { Application } from '../../../Mappers/ApplicationFormMapper'
import { submitFromlessApplication } from '../../../utils/formless-utils'
import { fireReactGAEvent } from '../../Questions/utils'
import { LenderDetails } from '../ApplicationFormContext'

export type FormlessJourneyProps = {
  experimentName: string
  attribution: AttributionDetails
  submissionResponse?: Record<string, any>
  isPreapproved: boolean
  eventsServiceUrl: string
  appFormEventParams?: AppFormEventParams
  mappedApplication?: Application
  lenderDetails?: LenderDetails
  shouldShowConfetti?: boolean
}

export const FormlessJourney = ({
  experimentName,
  attribution,
  submissionResponse,
  isPreapproved = false,
  eventsServiceUrl,
  appFormEventParams,
  mappedApplication
}: FormlessJourneyProps): JSX.Element => {
  const [apiError, setApiError] = useState(false)
  const [response, setResponse] = useState(submissionResponse)
  const eventsService = appFormEventParams && EventsService(eventsServiceUrl, appFormEventParams)

  useEffect(() => {
    if (!response?.success) {
      setTimeout(() => {
        setApiError(true)
      }, 3000)
    }
    if (response?.redirectUrl && response.success) {
      const redirectUrl = new URL(response.redirectUrl)
      const nextUrl = redirectUrl.searchParams.get('nextUrl')

      if (nextUrl) {
        // This covers the pre-auth part for when redirecting to join-zuto (new users)
        const decodedNextUrl = decodeURIComponent(nextUrl)
        let updatedNextUrl = decodedNextUrl

        if (isPreapproved) {
          updatedNextUrl = decodedNextUrl.includes('?')
            ? `${decodedNextUrl}&preapproved=true`
            : `${decodedNextUrl}?preapproved=true`
        }

        redirectUrl.searchParams.set('nextUrl', updatedNextUrl)
      } else if (isPreapproved) {
        // Appended directly to redirect url for when redirecting to sign-in (different to pre-auth above)
        redirectUrl.searchParams.append('preapproved', 'true')
      }

      setTimeout(() => {
        window.location.replace(redirectUrl.toString())
      }, 5000)
    }
  }, [response, isPreapproved])

  const closeError = async () => {
    // re-submit app
    if (mappedApplication && eventsService) {
      setApiError(false)
      const response = await submitFromlessApplication({
        application: mappedApplication,
        eventsService,
        isRetry: true
      })
      fireReactGAEvent({
        category: 'Application Form',
        action: 'Submit',
        label: response.success ? 'Success' : 'Unsuccessful'
      })
      setResponse(response)
    }
  }

  return (
    <>
      {apiError && (
        <ErrorModal titleText="Sorry" ctaText="Please try again" onClick={closeError}>
          There was a problem submitting your application
        </ErrorModal>
      )}
      {!apiError && attribution && (
        <FormlessInterstitialWrapper
          source={attribution.source}
          experimentName={experimentName}
          isPreapproved={isPreapproved}
        />
      )}
    </>
  )
}
